import { useState, useMemo, useEffect, useRef } from "react";
import {
  ComposedChart, Area, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, ReferenceArea
} from "recharts";

const CURRENT_YEAR = 2026;
const C = {
  bg:"#F9F7F4", paper:"#FFFFFF", text:"#2C2A27",
  sageDark:"#3D4F41", sage:"#5F7161", sageMid:"#8FA892",
  oak:"#B68D40", oakLight:"#D4AB60",
  cream:"#EFD9B4", creamBg:"#FAF5EC",
  alert:"#C4622D", alertBg:"#FDF1E9",
  muted:"#8A8678", border:"#E2DDD5",
  green:"#4A7C59", greenBg:"#F0F6F2",
};

function useIsMobile(bp = 680) {
  const [v, setV] = useState(typeof window !== "undefined" ? window.innerWidth < bp : false);
  useEffect(() => {
    const h = () => setV(window.innerWidth < bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return v;
}

/* ── Gauge ── */
function Gauge({ value, maxValue, monthlyPayment, helocRate }) {
  const pct = Math.min(1, Math.max(0, value / (maxValue || 1)));
  // cy near bottom of viewBox so the upward arc has plenty of room above
  const cx = 120, cy = 108, r = 88;
  const alpha = pct * Math.PI;
  // Points on the TOP semicircle:
  //   alpha=0   → left end  (cx-r, cy)
  //   alpha=π/2 → top peak  (cx, cy-r)
  //   alpha=π   → right end (cx+r, cy)
  const fX = cx - r * Math.cos(alpha);
  const fY = cy - r * Math.sin(alpha);
  const nX = cx - 68 * Math.cos(alpha);
  const nY = cy - 68 * Math.sin(alpha);
  const col = pct > 0.65 ? C.oak : pct > 0.3 ? C.sage : C.muted;
  // sweep=1 = clockwise in SVG screen coords = travels UPWARD from left to right ✓
  // largeArc must flip at 50% so we always take the short clockwise path
  const largeArc = pct > 0.5 ? 1 : 0;
  return (
    <div style={{ textAlign:"center" }}>
      {/* Rate context badge */}
      <div style={{ display:"inline-flex", alignItems:"center", gap:"6px",
        background:C.creamBg, border:`1px solid ${C.border}`,
        borderRadius:"20px", padding:"4px 12px", marginBottom:"6px" }}>
        <div style={{ width:"6px", height:"6px", borderRadius:"50%", background:C.oak }} />
        <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:"13px",
          fontWeight:600, color:C.oak }}>
          Current avg HELOC rates: 8–10% APR
        </span>
      </div>

      {/* viewBox height = cy + label clearance = 108 + 22 = 130 */}
      <svg width="240" height="130" viewBox="0 0 240 130"
        style={{ display:"block", margin:"0 auto" }}>
        {/* Full track: left → top → right, clockwise, sweep=1 */}
        <path d={`M ${cx-r} ${cy} A ${r} ${r} 0 0 1 ${cx+r} ${cy}`}
          fill="none" stroke={C.border} strokeWidth="13" strokeLinecap="round" />
        {/* Colored fill: left → fX,fY, clockwise */}
        {pct > 0.008 && (
          <path d={`M ${cx-r} ${cy} A ${r} ${r} 0 ${largeArc} 1 ${fX} ${fY}`}
            fill="none" stroke={col} strokeWidth="13" strokeLinecap="round"
            style={{ transition:"all 0.45s cubic-bezier(.4,0,.2,1)" }} />
        )}
        {/* Tick marks */}
        {[0, .25, .5, .75, 1].map(t => {
          const a = t * Math.PI;
          return <line key={t}
            x1={cx-(r+8)*Math.cos(a)} y1={cy-(r+8)*Math.sin(a)}
            x2={cx-(r+1)*Math.cos(a)} y2={cy-(r+1)*Math.sin(a)}
            stroke={C.muted} strokeWidth="1.5" strokeOpacity="0.35" />;
        })}
        {/* Needle */}
        <line x1={cx} y1={cy} x2={nX} y2={nY}
          stroke={C.text} strokeWidth="2.5" strokeLinecap="round"
          style={{ transition:"all 0.45s cubic-bezier(.4,0,.2,1)" }} />
        <circle cx={cx} cy={cy} r="6" fill={C.text} />
        <circle cx={cx} cy={cy} r="2.5" fill={C.paper} />
        {/* Labels */}
        <text x={cx-r-3} y={cy+16} fontSize="10" fill={C.muted}
          textAnchor="end" fontFamily="Montserrat,sans-serif">$0</text>
        <text x={cx+r+3} y={cy+16} fontSize="10" fill={C.muted}
          textAnchor="start" fontFamily="Montserrat,sans-serif">
          ${Math.round(maxValue/1000)}k
        </text>
      </svg>

      <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"30px",
        fontWeight:700, color:col, lineHeight:1, marginTop:"2px",
        transition:"color 0.45s" }}>
        ${value.toLocaleString("en-US")}
      </div>
      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
        letterSpacing:"0.14em", color:C.muted, textTransform:"uppercase", marginTop:"5px" }}>
        Estimated Accessible Equity
      </div>
      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
        color:C.muted, marginTop:"2px" }}>At an 85% Combined LTV ratio</div>

      {/* Monthly payment preview under gauge */}
      {monthlyPayment > 0 && (
        <div style={{ marginTop:"12px", background:C.creamBg, borderRadius:"6px",
          padding:"10px 14px", border:`1px solid ${C.border}` }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
            fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase",
            color:C.muted, marginBottom:"4px" }}>
            Est. Interest-Only Payment
          </div>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"22px",
            fontWeight:700, color:C.oak }}>
            ${monthlyPayment.toLocaleString()}<span style={{
              fontFamily:"Montserrat,sans-serif", fontSize:"12px",
              fontWeight:400, color:C.muted }}>/mo</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── DTI Panel ── */
function DTIPanel({ cliffActive, hasAbatement, abatExpiry, yearsToCliff,
  currentDTI, postCliffDTI, taxIncrease, dtiWithHeloc, helocPayment }) {
  const dtiColor = (d) => d < 36 ? C.green : d < 43 ? C.oak : C.alert;
  return (
    <div style={{ flex:1, background:cliffActive ? C.greenBg : C.bg,
      border:`1px solid ${cliffActive ? C.sage+"55" : C.border}`,
      borderRadius:"6px", padding:"16px 18px",
      display:"flex", flexDirection:"column", justifyContent:"flex-start", gap:"12px" }}>

      {cliffActive && (
        <div style={{ display:"flex", alignItems:"center", gap:"7px" }}>
          <div style={{ width:"7px", height:"7px", borderRadius:"50%",
            background:C.sage, boxShadow:`0 0 0 3px rgba(95,113,97,.2)`, flexShrink:0 }} />
          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
            fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:C.sage }}>
            DTI &amp; HELOC — Optimal Window Active
          </span>
        </div>
      )}

      {/* DTI before/after */}
      <div style={{ display:"flex", gap:"10px" }}>
        {/* DTI Now — always shown */}
        <div style={{ flex:1, background:C.paper, borderRadius:"5px",
          padding:"10px", border:`1px solid ${C.border}` }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
            fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase",
            color:C.muted, marginBottom:"4px" }}>DTI Now</div>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"22px",
            fontWeight:700, color:dtiColor(currentDTI) }}>
            {currentDTI.toFixed(1)}%
          </div>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
            color:C.muted, marginTop:"2px" }}>
            {currentDTI < 36 ? "✓ Strong" : currentDTI < 43 ? "~ Moderate" : "⚠ High"}
          </div>
        </div>

        {/* DTI After Abatement — greyed when abatement off */}
        <div style={{ flex:1, background:C.paper, borderRadius:"5px",
          padding:"10px",
          border:`1px solid ${hasAbatement ? "rgba(196,98,45,.25)" : C.border}`,
          opacity: hasAbatement ? 1 : 0.45,
          transition:"opacity 0.2s" }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
            fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase",
            color: hasAbatement ? C.alert : C.muted, marginBottom:"4px" }}>
            {hasAbatement ? `DTI After Abatement Expires` : "DTI After Abatement"}
          </div>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"22px",
            fontWeight:700, color: hasAbatement ? dtiColor(postCliffDTI) : C.muted }}>
            {hasAbatement ? `${postCliffDTI.toFixed(1)}%` : "—"}
          </div>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
            color:C.muted, marginTop:"2px" }}>
            {hasAbatement
              ? `+$${taxIncrease.toLocaleString()}/mo taxes`
              : "Enable abatement above"}
          </div>
        </div>
      </div>

      {/* With HELOC payment row */}
      {helocPayment > 0 && (
        <div style={{ background:C.paper, borderRadius:"5px",
          padding:"10px 12px", border:`1px solid ${C.border}` }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
            fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase",
            color:C.muted, marginBottom:"6px" }}>
            DTI Including Your HELOC Payment
          </div>
          <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
            <div>
              <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px", color:C.muted }}>Now</div>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"22px",
                fontWeight:700, color:dtiColor(dtiWithHeloc) }}>{dtiWithHeloc.toFixed(1)}%</div>
            </div>
            <div style={{ color:C.muted, fontSize:"12px" }}>→</div>
            <div>
              <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px", color:C.alert }}>
                After {abatExpiry}
              </div>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"22px",
                fontWeight:700, color:dtiColor(parseFloat(dtiWithHeloc) + (postCliffDTI - currentDTI)) }}>
                {(parseFloat(dtiWithHeloc) + (postCliffDTI - currentDTI)).toFixed(1)}%
              </div>
            </div>
          </div>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
            color:C.muted, marginTop:"6px", lineHeight:1.5 }}>
            Most lenders approve HELOCs up to 43% DTI. Apply now while your DTI is lower.
          </div>
        </div>
      )}

      {cliffActive && (
        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
          color:C.muted, lineHeight:1.6 }}>
          Apply before taxes reset in{" "}
          <strong style={{color:C.alert}}>{abatExpiry}</strong>.
          Your borrowing power will narrow as DTI rises.
        </div>
      )}
    </div>
  );
}

/* ── Payment Estimator Card ── */
function PaymentCard({ accessibleEquity, borrowAmount, setBorrowAmount,
  helocRate, setHelocRate, monthlyPayment }) {

  const uses = useMemo(() => {
    const all = [
      { min:5000,  icon:"🚿", label:"Bathroom remodel",       est:"$8k–$20k"  },
      { min:20000, icon:"🍳", label:"Full kitchen renovation", est:"$20k–$50k" },
      { min:50000, icon:"🏡", label:"Room addition or ADU",    est:"$50k+"     },
      { min:3000,  icon:"💰", label:"6-month emergency fund",  est:"3–6 mo. expenses" },
      { min:15000, icon:"📈", label:"Investment account seed", est:"Brokerage/IRA"    },
      { min:10000, icon:"💳", label:"High-interest debt payoff",est:"Credit cards/auto"},
      { min:30000, icon:"🏘️", label:"Rental property down pmt",est:"$25k–$40k" },
    ];
    return all.filter(u => borrowAmount >= u.min).slice(0, 4);
  }, [borrowAmount]);

  return (
    <div style={{ background:C.paper, border:`1px solid ${C.border}`,
      borderRadius:"6px", padding:"20px 22px" }}>
      <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"22px",
        fontWeight:600, color:C.text, marginBottom:"16px" }}>
        What Could This Equity Do For You?
      </div>

      <div style={{ display:"flex", gap:"20px", flexWrap:"wrap" }}>
        {/* Left: sliders */}
        <div style={{ flex:"1 1 200px", minWidth:"180px" }}>
          <div style={{ marginBottom:"16px" }}>
            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
              fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase",
              color:C.muted, marginBottom:"5px" }}>I Want to Borrow</div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"24px",
              fontWeight:700, color:C.oak, marginBottom:"8px" }}>
              ${borrowAmount.toLocaleString()}
            </div>
            <input type="range" min={5000} max={Math.max(5000, Math.floor(accessibleEquity))}
              step={1000} value={Math.min(borrowAmount, accessibleEquity)}
              onChange={e => setBorrowAmount(Number(e.target.value))}
              style={{ WebkitAppearance:"none", width:"100%", height:"4px",
                borderRadius:"2px", outline:"none", cursor:"pointer",
                background:`linear-gradient(to right,${C.oak} 0%,${C.oak}
                  ${Math.min(100,(borrowAmount/Math.max(1,accessibleEquity))*100)}%,
                  ${C.border}
                  ${Math.min(100,(borrowAmount/Math.max(1,accessibleEquity))*100)}%,
                  ${C.border} 100%)` }} />
            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
              color:C.muted, marginTop:"4px" }}>
              Max available: <strong>${Math.floor(accessibleEquity).toLocaleString()}</strong>
            </div>
          </div>

          <div style={{ marginBottom:"4px" }}>
            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
              fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase",
              color:C.muted, marginBottom:"5px" }}>HELOC Rate (APR)</div>
            <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
              <input type="range" min={5} max={12} step={0.1} value={helocRate}
                onChange={e => setHelocRate(Number(e.target.value))}
                style={{ WebkitAppearance:"none", flex:1, height:"4px",
                  borderRadius:"2px", outline:"none", cursor:"pointer",
                  background:`linear-gradient(to right,${C.sage} 0%,${C.sage}
                    ${((helocRate-5)/7)*100}%,${C.border} ${((helocRate-5)/7)*100}%,
                    ${C.border} 100%)` }} />
              <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:"13px",
                fontWeight:600, color:C.text, minWidth:"36px" }}>
                {helocRate.toFixed(1)}%
              </span>
            </div>
          </div>

          {/* Monthly payment result */}
          <div style={{ marginTop:"16px", background:C.sageDark, borderRadius:"6px",
            padding:"14px 16px" }}>
            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
              letterSpacing:"0.12em", textTransform:"uppercase",
              color:"rgba(255,255,255,.5)", marginBottom:"4px" }}>
              Est. Monthly Payment
            </div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"28px",
              fontWeight:700, color:C.oakLight, lineHeight:1 }}>
              ${monthlyPayment.toLocaleString()}
              <span style={{ fontFamily:"Montserrat,sans-serif",
                fontSize:"12px", fontWeight:400, color:"rgba(255,255,255,.5)" }}>/mo</span>
            </div>
            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
              color:"rgba(255,255,255,.4)", marginTop:"4px" }}>
              Interest-only · adjust rate above
            </div>
          </div>
        </div>

        {/* Right: use cases */}
        {uses.length > 0 && (
          <div style={{ flex:"1 1 180px", minWidth:"160px" }}>
            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
              fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase",
              color:C.muted, marginBottom:"10px" }}>
              ${borrowAmount.toLocaleString()} Could Fund
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
              {uses.map(u => (
                <div key={u.label} style={{ display:"flex", alignItems:"flex-start",
                  gap:"10px", background:C.bg, borderRadius:"5px", padding:"10px 12px",
                  border:`1px solid ${C.border}` }}>
                  <span style={{ fontSize:"18px", flexShrink:0, lineHeight:1.2 }}>{u.icon}</span>
                  <div>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"13px",
                      fontWeight:600, color:C.text }}>{u.label}</div>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
                      color:C.muted, marginTop:"1px" }}>{u.est}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Chart Tooltip ── */
function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  const netEquity = d.totalEquity - (d.helocBalance || 0);
  return (
    <div style={{ background:C.text, color:"#fff", padding:"11px 14px", borderRadius:"4px",
      fontFamily:"Montserrat,sans-serif", fontSize:"13px", minWidth:"185px",
      boxShadow:"0 6px 20px rgba(0,0,0,.25)" }}>
      <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"13px",
        fontWeight:700, marginBottom:"8px", color:C.cream }}>{d.label}</div>
      {[["Rem. Balance",C.sageMid,d.balance],
        ["Paid Principal",C.oakLight,d.principalPaid],
        ["Appreciation",C.cream,d.appreciation]].map(([l,c,v])=>(
        <div key={l} style={{ display:"flex", justifyContent:"space-between", marginBottom:"3px" }}>
          <span style={{color:c}}>{l}</span><span>${v.toLocaleString()}</span>
        </div>
      ))}
      {d.helocBalance > 0 && (
        <div style={{ display:"flex", justifyContent:"space-between",
          marginBottom:"3px", color:"#F4A07A" }}>
          <span>HELOC Balance</span><span>-${d.helocBalance.toLocaleString()}</span>
        </div>
      )}
      <div style={{ borderTop:"1px solid rgba(255,255,255,.15)", paddingTop:"6px",
        marginTop:"6px", display:"flex", justifyContent:"space-between", fontWeight:700 }}>
        <span>{d.helocBalance > 0 ? "Net Equity" : "Total Equity"}</span>
        <span style={{color: d.helocBalance > 0 ? "#F4A07A" : C.oakLight}}>
          ${(d.helocBalance > 0 ? netEquity : d.totalEquity).toLocaleString()}
        </span>
      </div>
      {d.postCliffEquity != null && d.postCliffEquity !== d.totalEquity && (
        <div style={{ display:"flex", justifyContent:"space-between",
          color:"rgba(196,98,45,.9)", marginTop:"3px", fontSize:"13px" }}>
          <span>Post-Cliff Scenario</span>
          <span>${d.postCliffEquity.toLocaleString()}</span>
        </div>
      )}
    </div>
  );
}

/* ══════════════════ MAIN APP ══════════════════ */
export default function EquityRoadmap() {
  const isMobile = useIsMobile();
  const chartRef = useRef(null);
  const [emailStatus, setEmailStatus] = useState("idle"); // idle | loading | success | error
  const [emailError, setEmailError] = useState("");

  // Core inputs
  const [purchaseYear,  setPurchaseYear]  = useState(2021);
  const [purchasePrice, setPurchasePrice] = useState(320000);
  const [downPayment,   setDownPayment]   = useState(30000);
  const [interestRate,  setInterestRate]  = useState(5.99);
  const [currentValue,  setCurrentValue]  = useState(390000);
  const [appRate,       setAppRate]       = useState(3.5);

  // Tax cliff inputs
  const [hasAbatement,       setHasAbatement]       = useState(false);
  const [abatExpiry,         setAbatExpiry]         = useState(2028);
  const [currentMonthlyTax,  setCurrentMonthlyTax]  = useState(150);
  const [postCliffMonthlyTax,setPostCliffMonthlyTax]= useState(580);

  // DTI
  const [monthlyIncome, setMonthlyIncome] = useState(8500);

  // Payment estimator
  const [borrowAmount,   setBorrowAmount]   = useState(40000);
  const [helocRate,      setHelocRate]      = useState(8.5);
  const [drawPeriod,     setDrawPeriod]     = useState(10);
  const [repayPeriod,    setRepayPeriod]    = useState(15);
  const [helocScenario,  setHelocScenario]  = useState(1); // 1=interest-only, 2=extra principal, 3=pay off in draw
  const [extraPrincipal, setExtraPrincipal] = useState(200);
  const [showHelocOverlay, setShowHelocOverlay] = useState(true);

  // Chart toggle
  const [showPostCliff, setShowPostCliff] = useState(false);

  const [email, setEmail] = useState("");
  const [zipCode, setZipCode] = useState("");

  /* ── Derived ── */
  const loanAmount   = Math.max(0, purchasePrice - downPayment);
  const yearsOwned   = CURRENT_YEAR - purchaseYear;
  const yearsToCliff = abatExpiry - CURRENT_YEAR;
  const hasCliff     = hasAbatement && abatExpiry > purchaseYear && abatExpiry <= purchaseYear + 30;
  const cliffActive  = hasCliff && yearsToCliff > 0;

  // Implied appreciation based on actual purchase price vs current value
  const impliedAppRate = yearsOwned > 0 && purchasePrice > 0
    ? ((currentValue - purchasePrice) / purchasePrice / yearsOwned * 100)
    : 0;

  // Appreciation benchmark label
  const appBenchmark = appRate < 2
    ? { label:"Below avg", color:C.alert }
    : appRate <= 4
      ? { label:"Near avg", color:C.sage }
      : { label:"Above avg", color:C.oak };
  const effectiveCurrentTax  = hasAbatement ? currentMonthlyTax  : postCliffMonthlyTax;
  const effectivePostTax     = hasAbatement ? postCliffMonthlyTax : postCliffMonthlyTax;

  const monthlyMortgage = useMemo(() => {
    const mRate = interestRate / 100 / 12, n = 360;
    return loanAmount > 0 && mRate > 0
      ? (loanAmount * (mRate * Math.pow(1+mRate,n))) / (Math.pow(1+mRate,n)-1)
      : 0;
  }, [loanAmount, interestRate]);

  const taxIncrease  = hasAbatement ? Math.max(0, postCliffMonthlyTax - currentMonthlyTax) : 0;
  const currentDTI   = monthlyIncome > 0 ? (monthlyMortgage + effectiveCurrentTax) / monthlyIncome * 100 : 0;
  const postCliffDTI = monthlyIncome > 0 ? (monthlyMortgage + effectivePostTax) / monthlyIncome * 100 : 0;
  const helocPayment = Math.round(borrowAmount * helocRate / 100 / 12);
  const dtiWithHeloc = monthlyIncome > 0 ? (monthlyMortgage + effectiveCurrentTax + helocPayment) / monthlyIncome * 100 : 0;

  /* ── HELOC balance by year ── */
  const helocCalc = useMemo(() => {
    const hRate = helocRate / 100 / 12;
    const drawMos = drawPeriod * 12;
    const repayMos = repayPeriod * 12;

    // Scenario 3: monthly payment to fully pay off in draw period
    const s3Monthly = borrowAmount > 0 && hRate > 0
      ? (borrowAmount * (hRate * Math.pow(1+hRate, drawMos))) / (Math.pow(1+hRate, drawMos) - 1)
      : borrowAmount / drawMos;

    // Compute balance at each month from open (month 0 = now)
    const balByMonth = [borrowAmount];
    let bal = borrowAmount;
    for (let mo = 1; mo <= drawPeriod * 12 + repayPeriod * 12; mo++) {
      const interest = bal * hRate;
      if (mo <= drawMos) {
        // Draw period
        if (helocScenario === 1) {
          // interest only — balance unchanged, no-op
        } else if (helocScenario === 2) {
          bal = Math.max(0, bal - extraPrincipal); // pay extra principal
        } else {
          // Scenario 3: pay off in draw period
          const principal = Math.min(s3Monthly - interest, bal);
          bal = Math.max(0, bal - principal);
        }
      } else {
        // Repayment period — amortize whatever is left
        if (bal > 0) {
          const mosLeft = repayMos - (mo - drawMos);
          if (mosLeft > 0) {
            const repayPayment = hRate > 0
              ? (bal * (hRate * Math.pow(1+hRate, mosLeft))) / (Math.pow(1+hRate, mosLeft) - 1)
              : bal / mosLeft;
            const principal = Math.min(repayPayment - interest, bal);
            bal = Math.max(0, bal - principal);
          } else {
            bal = 0;
          }
        }
      }
      balByMonth.push(Math.round(bal));
    }

    // Balance at end of draw period → used for repayment shock calc
    const balAtDrawEnd = balByMonth[drawMos] || 0;
    const repayMonthly = balAtDrawEnd > 0 && hRate > 0
      ? Math.round((balAtDrawEnd * (hRate * Math.pow(1+hRate, repayMos)))
          / (Math.pow(1+hRate, repayMos) - 1))
      : 0;

    // Total cost across both phases
    const drawPayment = helocScenario === 1 ? Math.round(borrowAmount * hRate * 12 / 12)
      : helocScenario === 2 ? Math.round(borrowAmount * hRate * 12 / 12) + extraPrincipal
      : Math.round(s3Monthly);
    const totalCost = Math.round(
      helocScenario === 3
        ? s3Monthly * drawMos
        : (drawPayment * drawMos) + (repayMonthly * repayMos)
    );

    // Map to yearly (relative to purchaseYear)
    const balByYear = Array.from({ length: 31 }, (_, yr) => {
      const mosSinceOpen = (yr - yearsOwned) * 12;
      if (mosSinceOpen < 0) return 0;
      const idx = Math.min(mosSinceOpen, balByMonth.length - 1);
      return balByMonth[idx];
    });

    // Draw period end year + repay period end year (absolute)
    const drawEndYear  = CURRENT_YEAR + drawPeriod;
    const repayEndYear = CURRENT_YEAR + drawPeriod + repayPeriod;

    // Interest-only monthly during draw (for display)
    const drawMonthly  = Math.round(borrowAmount * helocRate / 100 / 12);
    const s3MonthlyRnd = Math.round(s3Monthly);

    return { balByYear, repayMonthly, drawEndYear, repayEndYear,
             drawMonthly, s3Monthly: s3MonthlyRnd, balAtDrawEnd, totalCost };
  }, [borrowAmount, helocRate, drawPeriod, repayPeriod, helocScenario,
      extraPrincipal, yearsOwned]);


  const chartData = useMemo(() => {
    const mRate = interestRate / 100 / 12, n = 360;
    const monthly = loanAmount > 0 && mRate > 0
      ? (loanAmount * (mRate * Math.pow(1+mRate,n))) / (Math.pow(1+mRate,n)-1)
      : loanAmount / n;
    const cliffYr = hasCliff ? abatExpiry - purchaseYear : 999;
    let bal = loanAmount, paid = downPayment;
    return Array.from({ length:31 }, (_,yr) => {
      if (yr > 0) {
        for (let mo = 0; mo < 12; mo++) {
          const interest = bal * mRate;
          const principal = Math.min(monthly - interest, bal);
          paid += principal; bal = Math.max(0, bal - principal);
        }
      }
      const projValue = currentValue * Math.pow(1 + appRate/100, yr);
      const appreciation = Math.max(0, projValue - purchasePrice);
      // Post-cliff scenario: appreciation rate drops 35% after cliff (higher holding costs)
      const postCliffAppRate = appRate * 0.65;
      const projValuePostCliff = yr <= cliffYr
        ? projValue
        : currentValue * Math.pow(1 + appRate/100, cliffYr)
            * Math.pow(1 + postCliffAppRate/100, yr - cliffYr);
      const appreciationPostCliff = Math.max(0, projValuePostCliff - purchasePrice);
      return {
        year: purchaseYear + yr,
        label: String(purchaseYear + yr),
        balance:           Math.round(Math.max(0, bal)),
        principalPaid:     Math.round(paid),
        appreciation:      Math.round(appreciation),
        totalEquity:       Math.round(paid + appreciation),
        postCliffEquity:   Math.round(paid + appreciationPostCliff),
        helocBalance:      showHelocOverlay ? (helocCalc.balByYear[yr] || 0) : 0,
      };
    });
  }, [loanAmount, interestRate, currentValue, appRate, downPayment,
      purchaseYear, purchasePrice, hasCliff, abatExpiry,
      showHelocOverlay, helocCalc]);

  // PMI crossover — year balance drops below 80% of purchase price
  const pmiCrossoverYear = useMemo(() => {
    const threshold = purchasePrice * 0.8;
    const entry = chartData.find(d => d.balance <= threshold && d.balance > 0);
    return entry ? entry.year : null;
  }, [chartData, purchasePrice]);

  const nowData = chartData[Math.max(0, Math.min(yearsOwned, 30))];
  const accessibleEquity = Math.max(0, currentValue * 0.85 - nowData.balance);
  const fmt$ = v => `$${(v/1000).toFixed(0)}k`;
  /* ── Email submit handler ── */
  const handleSubmit = async () => {
    if (!email || !email.includes("@")) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");
    setEmailStatus("loading");

    try {
      // Step 1: Screenshot the chart using html2canvas
      let chartImageBase64 = null;
      if (chartRef.current) {
        const html2canvas = (await import("html2canvas")).default;
        const canvas = await html2canvas(chartRef.current, {
          backgroundColor: C.paper,
          scale: 2,
          logging: false,
          useCORS: true,
        });
        chartImageBase64 = canvas.toDataURL("image/png");
      }

      // Step 2: Build PDF using jsPDF
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });

      // Load and register custom fonts
      const loadFont = async (url) => {
        const res = await fetch(url);
        const buf = await res.arrayBuffer();
        const binary = new Uint8Array(buf);
        let str = "";
        for (let i = 0; i < binary.length; i++) str += String.fromCharCode(binary[i]);
        return btoa(str);
      };

      try {
        const [playfairB64, montserratRegB64, montserratSemB64] = await Promise.all([
          loadFont("/fonts/PlayfairDisplay-Bold.ttf"),
          loadFont("/fonts/Montserrat-Regular.ttf"),
          loadFont("/fonts/Montserrat-SemiBold.ttf"),
        ]);
        doc.addFileToVFS("PlayfairDisplay-Bold.ttf", playfairB64);
        doc.addFont("PlayfairDisplay-Bold.ttf", "PlayfairDisplay", "bold");
        doc.addFileToVFS("Montserrat-Regular.ttf", montserratRegB64);
        doc.addFont("Montserrat-Regular.ttf", "Montserrat", "normal");
        doc.addFileToVFS("Montserrat-SemiBold.ttf", montserratSemB64);
        doc.addFont("Montserrat-SemiBold.ttf", "Montserrat", "bold");
      } catch(fontErr) {
        console.warn("Custom fonts failed to load, falling back to helvetica", fontErr);
      }

      // Font helpers — fall back gracefully if custom fonts didn't load
      const fonts = doc.getFontList();
      const hasPlayfair = !!fonts["PlayfairDisplay"];
      const hasMontserrat = !!fonts["Montserrat"];
      const serif = hasPlayfair ? "PlayfairDisplay" : "helvetica";
      const sans  = hasMontserrat ? "Montserrat" : "helvetica";

      const W = doc.internal.pageSize.getWidth();
      const margin = 48;
      let y = margin;

      // ── Header band ──
      doc.setFillColor(61, 79, 65);
      doc.rect(0, 0, W, 80, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFont(serif, "bold");
      doc.setFontSize(28);
      doc.text("found", margin, 50);
      doc.setTextColor(212, 171, 96);
      doc.text("HE", margin + doc.getTextWidth("found"), 50);

      doc.setTextColor(200, 200, 200);
      doc.setFont(sans, "normal");
      doc.setFontSize(10);
      doc.text("HOME EQUITY. UNDERSTOOD.", margin, 66);

      doc.setTextColor(180, 180, 180);
      doc.setFontSize(9);
      doc.text(`Generated ${new Date().toLocaleDateString("en-US", { month:"long", day:"numeric", year:"numeric" })}`,
        W - margin, 66, { align:"right" });

      y = 110;

      // ── Section: Your Scenario ──
      doc.setFillColor(249, 247, 244); // bg
      doc.rect(margin, y, W - margin*2, 180, "F");
      doc.setDrawColor(226, 221, 213); // border
      doc.rect(margin, y, W - margin*2, 180);

      doc.setTextColor(61, 79, 65);
      doc.setFont(serif, "bold");
      doc.setFontSize(11);
      doc.text("YOUR SCENARIO", margin + 16, y + 22);

      const scenarioData = [
        ["Purchase Price", `$${purchasePrice.toLocaleString()}`],
        ["Down Payment", `$${downPayment.toLocaleString()}`],
        ["Current Value", `$${currentValue.toLocaleString()}`],
        ["Remaining Balance", `$${(nowData?.balance || 0).toLocaleString()}`],
        ["Interest Rate", `${interestRate.toFixed(2)}%`],
        ["Years Owned", `${yearsOwned}`],
        ["Annual Appreciation", `${appRate.toFixed(1)}%/yr`],
      ];

      const colW = (W - margin*2 - 32) / 2;
      scenarioData.forEach(([label, value], i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const sx = margin + 16 + col * colW;
        const sy = y + 42 + row * 34;
        doc.setFont(sans, "normal");
        doc.setFontSize(9);
        doc.setTextColor(138, 134, 120);
        doc.text(label.toUpperCase(), sx, sy);
        doc.setFont(sans, "bold");
        doc.setFontSize(12);
        doc.setTextColor(44, 42, 39);
        doc.text(value, sx, sy + 14);
      });

      y += 196;

      // ── Section: Key Numbers ──
      const boxW = (W - margin*2 - 12) / 2;
      const boxH = 148; // reduced to fit chart on single page

      // Scenario pill config
      const pillConfigs = {
        1: { text: "⚠ Interest-only draw",    r:196, g:98,  b:45  },
        2: { text: "✓ Pay down during draw",   r:61,  g:79,  b:65  },
        3: { text: "✓ Pay off in draw period", r:74,  g:124, b:89  },
      };
      const pillCfg = pillConfigs[helocScenario] || pillConfigs[1];

      // Draw period monthly for display
      const drawDisplayMonthly = helocScenario === 1 ? helocCalc.drawMonthly
        : helocScenario === 2 ? helocCalc.drawMonthly + extraPrincipal
        : helocCalc.s3Monthly;

      // ── LEFT: Accessible Equity ──
      doc.setFillColor(240, 246, 242);
      doc.rect(margin, y, boxW, boxH, "F");
      doc.setDrawColor(95, 113, 97);
      doc.setLineWidth(1.5);
      doc.rect(margin, y, boxW, boxH);
      doc.setLineWidth(0.5);

      // Label
      doc.setFont(sans, "bold");
      doc.setFontSize(9);
      doc.setTextColor(95, 113, 97);
      doc.text("ACCESSIBLE EQUITY (85% CLTV)", margin + 14, y + 18);

      // Ghost pill spacer (invisible, matches pill height)
      doc.setFillColor(240, 246, 242);
      doc.rect(margin + 14, y + 22, 60, 14, "F");

      // Big number
      doc.setFont(serif, "bold");
      doc.setFontSize(22);
      doc.setTextColor(44, 42, 39);
      doc.text(`$${accessibleEquity.toLocaleString()}`, margin + 14, y + 56);

      // Sub
      doc.setFont(sans, "normal");
      doc.setFontSize(9);
      doc.setTextColor(138, 134, 120);
      doc.text("Your estimated borrowing ceiling", margin + 14, y + 70);

      // Divider
      doc.setDrawColor(226, 221, 213);
      doc.line(margin + 14, y + 78, margin + boxW - 14, y + 78);

      // 3 data rows
      const nowData2 = chartData[Math.max(0, Math.min(yearsOwned, 30))];
      const ltv = nowData2 ? Math.round((nowData2.balance / currentValue) * 1000) / 10 : 0;
      [
        ["Current value",     `$${currentValue.toLocaleString()}`],
        ["Remaining balance", `$${(nowData2?.balance || 0).toLocaleString()}`],
        ["Loan-to-value",     `${ltv}%`],
      ].forEach(([label, val], i) => {
        const ry = y + 92 + i * 22;
        doc.setFont(sans, "normal"); doc.setFontSize(9); doc.setTextColor(138, 134, 120);
        doc.text(label, margin + 14, ry);
        doc.setFont(sans, "bold"); doc.setFontSize(12); doc.setTextColor(44, 42, 39);
        doc.text(val, margin + boxW - 14, ry, { align:"right" });
      });

      // ── RIGHT: HELOC Payment ──
      const rx = margin + boxW + 12;
      doc.setFillColor(250, 245, 236);
      doc.rect(rx, y, boxW, boxH, "F");
      doc.setDrawColor(182, 141, 64);
      doc.setLineWidth(1.5);
      doc.rect(rx, y, boxW, boxH);
      doc.setLineWidth(0.5);

      // Label
      doc.setFont(sans, "bold");
      doc.setFontSize(9);
      doc.setTextColor(182, 141, 64);
      doc.text(`EST. HELOC PAYMENT @ ${helocRate.toFixed(1)}% APR`, rx + 14, y + 18);

      // Scenario pill
      doc.setFillColor(pillCfg.r, pillCfg.g, pillCfg.b);
      const pillW = doc.getTextWidth(pillCfg.text) + 14;
      doc.roundedRect(rx + 14, y + 22, pillW, 14, 2, 2, "F");
      doc.setFont(sans, "bold");
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(pillCfg.text, rx + 21, y + 31);

      // Big number with /mo suffix in Montserrat
      doc.setFont(serif, "bold");
      doc.setFontSize(22);
      doc.setTextColor(44, 42, 39);
      const numStr = `$${drawDisplayMonthly.toLocaleString()}`;
      doc.text(numStr, rx + 14, y + 56);
      const numW = doc.getTextWidth(numStr);
      doc.setFont(sans, "normal");
      doc.setFontSize(12);
      doc.setTextColor(138, 134, 120);
      doc.text("/mo", rx + 14 + numW + 2, y + 56);

      // Sub
      doc.setFont(sans, "normal");
      doc.setFontSize(9);
      doc.setTextColor(138, 134, 120);
      const subLabel = helocScenario === 1 ? `Interest-only · $${borrowAmount.toLocaleString()} borrowed`
        : helocScenario === 2 ? `Interest + $${extraPrincipal} principal · $${borrowAmount.toLocaleString()} borrowed`
        : `Fully amortizing · $${borrowAmount.toLocaleString()} borrowed`;
      doc.text(subLabel, rx + 14, y + 70);

      // Divider
      doc.setDrawColor(226, 221, 213);
      doc.line(rx + 14, y + 78, rx + boxW - 14, y + 78);

      // 3 data rows
      const repayLabel = helocCalc.balAtDrawEnd > 0
        ? `$${helocCalc.repayMonthly.toLocaleString()}/mo`
        : "None ✓";
      const repayColor = helocScenario === 1 ? [196,98,45]
        : helocScenario === 3 ? [74,124,89] : [44,42,39];
      const helocRows = [
        ["Draw period",      `${drawPeriod} yrs`,                        [44,42,39]],
        ["Repayment phase",  repayLabel,                                   repayColor],
        ["Total cost",       `$${helocCalc.totalCost.toLocaleString()} paid`,
          helocScenario === 3 ? [74,124,89] : helocScenario === 1 ? [196,98,45] : [44,42,39]],
      ];
      helocRows.forEach(([label, val, color], i) => {
        const ry = y + 92 + i * 22;
        doc.setFont(sans, "normal"); doc.setFontSize(9); doc.setTextColor(138, 134, 120);
        doc.text(label, rx + 14, ry);
        doc.setFont(sans, "bold"); doc.setFontSize(12); doc.setTextColor(...color);
        doc.text(val, rx + boxW - 14, ry, { align:"right" });
      });

      y += boxH + 12;

      // ── DTI Row ──
      doc.setFillColor(249, 247, 244);
      doc.rect(margin, y, W - margin*2, 52, "F");
      doc.setDrawColor(226, 221, 213);
      doc.rect(margin, y, W - margin*2, 52);
      doc.setFont(sans, "bold");
      doc.setFontSize(9);
      doc.setTextColor(138, 134, 120);
      doc.text("DEBT-TO-INCOME RATIO (DTI)", margin + 16, y + 18);
      doc.setFont(sans, "bold");
      doc.setFontSize(20);
      const dtiCol = currentDTI < 36 ? [74, 124, 89] : currentDTI < 43 ? [182, 141, 64] : [196, 98, 45];
      doc.setTextColor(...dtiCol);
      doc.text(`${currentDTI.toFixed(1)}%`, margin + 16, y + 40);
      doc.setFont(sans, "normal");
      doc.setFontSize(10);
      doc.setTextColor(44, 42, 39);
      const dtiLabel = currentDTI < 36 ? "✓ Strong — most lenders will approve" : currentDTI < 43 ? "Moderate — may qualify with some lenders" : "High — consider paying down debt first";
      doc.text(dtiLabel, margin + 80, y + 40);

      y += 68;

      // ── Chart Image ──
      if (chartImageBase64) {
        doc.setFont(sans, "bold");
        doc.setFontSize(11);
        doc.setTextColor(61, 79, 65);
        doc.text("YOUR 30-YEAR EQUITY PROJECTION", margin, y + 18);
        y += 28;
        const chartH = 175; // reduced to clear footer
        const pageH = doc.internal.pageSize.getHeight();
        const footerH = 40;
        // Safety clamp — never let chart overlap footer
        const safeChartH = Math.min(chartH, pageH - footerH - 8 - y);
        doc.addImage(chartImageBase64, "PNG", margin, y, W - margin*2, safeChartH);

        y += safeChartH + 8;
      }

      // ── Footer ──
      doc.setFillColor(61, 79, 65);
      doc.rect(0, doc.internal.pageSize.getHeight() - 40, W, 40, "F");
      doc.setFont(sans, "normal");
      doc.setFontSize(9);
      doc.setTextColor(180, 180, 180);
      doc.text("foundHE.com  ·  Estimates only, not financial advice  ·  © 2026", margin, doc.internal.pageSize.getHeight() - 14);

      // Step 3: Get PDF as base64
      const pdfBase64 = doc.output("datauristring").split(",")[1];

      // Step 4: Send to serverless function
      const res = await fetch("/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          zipCode,
          pdfBase64,
          scenario: {
            purchasePrice, downPayment, currentValue,
            interestRate, appRate, accessibleEquity,
            helocPayment, helocRate, borrowAmount,
            currentDTI: currentDTI.toFixed(1),
            purchaseYear
          }
        }),
      });

      if (!res.ok) throw new Error("Email send failed");
      setEmailStatus("success");

    } catch (err) {
      console.error(err);
      setEmailStatus("error");
      setEmailError("Something went wrong — please try again.");
    }
  };

  const P = isMobile ? "16px" : "48px";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Montserrat:wght@300;400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
        input[type=range] { -webkit-appearance:none; }
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance:none; margin:0; }
        input[type=number] { -moz-appearance:textfield; }
        input[type=range]::-webkit-slider-thumb {
          -webkit-appearance:none; width:17px; height:17px; border-radius:50%;
          background:white; border:2.5px solid ${C.oak}; cursor:pointer;
          box-shadow:0 2px 6px rgba(0,0,0,.14); transition:transform .15s;
        }
        input[type=range]::-webkit-slider-thumb:hover { transform:scale(1.15); }
        select:focus { outline:none; }
        .cta-btn {
          background:${C.oak}; color:white; border:none; padding:13px 22px;
          font-family:Montserrat,sans-serif; font-size:12px; font-weight:700;
          letter-spacing:.14em; text-transform:uppercase; cursor:pointer;
          border-radius:3px; transition:all .2s; white-space:nowrap; width:100%;
        }
        .cta-btn:active { background:${C.oakLight}; }
        .email-inp {
          flex:1; padding:13px 14px; border:1px solid rgba(255,255,255,.15);
          border-radius:3px; background:rgba(255,255,255,.08); color:white;
          font-family:Montserrat,sans-serif; font-size:13px; outline:none; min-width:0; width:100%;
        }
        .email-inp::placeholder { color:rgba(255,255,255,.38); }
        .email-inp:focus { border-color:${C.oakLight}; }
        .toggle-btn {
          font-family:Montserrat,sans-serif; font-size:10px; font-weight:600;
          letter-spacing:0.08em; padding:5px 12px; border-radius:20px; cursor:pointer;
          border:1px solid; transition:all .2s;
        }
      `}</style>

      <div style={{ background:C.bg, minHeight:"100vh", fontFamily:"Montserrat,sans-serif" }}>
        <div style={{ maxWidth:"1200px", margin:"0 auto" }}>

        {/* HEADER */}
        <header style={{ background:C.sageDark, padding:`0 ${P}`, height:"72px",
          display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <div style={{ fontFamily:"'Playfair Display',serif",
              fontSize:isMobile?"20px":"26px", fontWeight:700, color:"white" }}>
              found<span style={{color:C.oakLight}}>HE</span>
            </div>
            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
              letterSpacing:"0.2em", color:"rgba(255,255,255,.42)",
              textTransform:"uppercase", marginTop:"2px" }}>
              Home Equity. Understood.
            </div>
          </div>
          {!isMobile && (
            <div style={{ fontFamily:"'Playfair Display',serif", fontStyle:"italic",
              fontSize:"15px", color:"rgba(255,255,255,.5)", textAlign:"right", lineHeight:1.5 }}>
              Your Home. Your Capital.<br/>Your Future.
            </div>
          )}
        </header>

        <div style={{ background:C.sageDark, paddingBottom:"24px",
          paddingLeft:P, paddingRight:P }}>
          <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
            color:"rgba(255,255,255,.4)", letterSpacing:"0.04em" }}>
            Know what you have before anyone asks you to apply for anything.
          </div>
        </div>

        {/* MAIN CARD */}
        <div style={{ padding:isMobile?"16px":"28px 48px" }}>
          <div style={{ background:C.paper, borderRadius:"8px",
            boxShadow:"0 2px 40px rgba(44,42,39,.09)",
            display:"flex", flexDirection:isMobile?"column":"row", overflow:"hidden" }}>

            {/* ── CONTROLS ── */}
            <div style={{
              width:isMobile?"100%":"288px", minWidth:isMobile?"unset":"288px",
              background:C.creamBg,
              borderRight:isMobile?"none":`1px solid ${C.border}`,
              borderBottom:isMobile?`1px solid ${C.border}`:"none",
              padding:isMobile?"16px":"24px"
            }}>
              {/* Header row: "Your Scenario" + Purchased In inline */}
              <div style={{ display:"flex", alignItems:"center",
                justifyContent:"space-between", marginBottom:"18px", gap:"12px" }}>
                <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"22px",
                  fontWeight:600, color:C.sageDark, whiteSpace:"nowrap" }}>
                  Your Scenario
                </div>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:"2px" }}>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
                    fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase",
                    color:C.muted }}>Purchased In</div>
                  <select value={purchaseYear} onChange={e=>setPurchaseYear(Number(e.target.value))}
                    style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
                      background:C.paper, border:`1px solid ${C.border}`,
                      borderRadius:"4px", padding:"6px 28px 6px 10px",
                      color:C.text, cursor:"pointer", appearance:"none",
                      backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%238A8678'/%3E%3C/svg%3E")`,
                      backgroundRepeat:"no-repeat", backgroundPosition:"right 9px center" }}>
                    {Array.from({length:24},(_,i)=>2002+i).map(y=>(
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 1: Purchase Price | Down Payment */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 20px" }}>

                {/* Purchase Price */}
                <div style={{ marginBottom:"20px" }}>
                  <div style={{fontFamily:"Montserrat,sans-serif",fontSize:"11px",fontWeight:700,
                    letterSpacing:"0.13em",textTransform:"uppercase",color:C.muted,marginBottom:"5px",minHeight:"30px",display:"flex",alignItems:"flex-end"}}>
                    Purchase Price
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:"2px",
                    marginBottom:"7px", height:"32px" }}>
                    <span style={{fontFamily:"'Playfair Display',serif",fontSize:"20px",fontWeight:700,color:C.text}}>$</span>
                    <input type="number" value={purchasePrice}
                      onChange={e=>setPurchasePrice(Number(e.target.value))}
                      onBlur={e=>setPurchasePrice(Math.max(80000,Math.min(1000000,Number(e.target.value)||purchasePrice)))}
                      style={{fontFamily:"'Playfair Display',serif",fontSize:"20px",fontWeight:700,
                        color:C.text,border:"none",borderBottom:`1px solid ${C.border}`,
                        background:"transparent",outline:"none",width:"100%",
                        MozAppearance:"textfield", height:"32px", padding:0}}/>
                  </div>
                  <input type="range" min={80000} max={1000000} step={5000} value={purchasePrice}
                    onChange={e=>setPurchasePrice(Number(e.target.value))}
                    style={{WebkitAppearance:"none",width:"100%",height:"4px",borderRadius:"2px",
                      outline:"none",cursor:"pointer",
                      background:`linear-gradient(to right,${C.sage} 0%,${C.sage} ${((purchasePrice-80000)/920000)*100}%,${C.border} ${((purchasePrice-80000)/920000)*100}%,${C.border} 100%)`}}/>
                  <div style={{fontFamily:"Montserrat,sans-serif",fontSize:"12px",color:C.muted,marginTop:"3px"}}>
                    Loan: <strong>${loanAmount.toLocaleString()}</strong>
                  </div>
                </div>

                {/* Down Payment */}
                <div style={{ marginBottom:"20px" }}>
                  <div style={{fontFamily:"Montserrat,sans-serif",fontSize:"11px",fontWeight:700,
                    letterSpacing:"0.13em",textTransform:"uppercase",color:C.muted,marginBottom:"5px",minHeight:"30px",display:"flex",alignItems:"flex-end"}}>
                    Down Payment
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:"2px",
                    marginBottom:"7px", height:"32px" }}>
                    <span style={{fontFamily:"'Playfair Display',serif",fontSize:"20px",fontWeight:700,color:C.text}}>$</span>
                    <input type="number" value={downPayment}
                      onChange={e=>setDownPayment(Number(e.target.value))}
                      onBlur={e=>setDownPayment(Math.max(0,Math.min(Math.floor(purchasePrice*.5),Number(e.target.value)||downPayment)))}
                      style={{fontFamily:"'Playfair Display',serif",fontSize:"20px",fontWeight:700,
                        color:C.text,border:"none",borderBottom:`1px solid ${C.border}`,
                        background:"transparent",outline:"none",width:"100%",
                        MozAppearance:"textfield", height:"32px", padding:0}}/>
                  </div>
                  <input type="range" min={0} max={Math.floor(purchasePrice*.5)} step={1000} value={downPayment}
                    onChange={e=>setDownPayment(Math.min(Number(e.target.value),purchasePrice))}
                    style={{WebkitAppearance:"none",width:"100%",height:"4px",borderRadius:"2px",
                      outline:"none",cursor:"pointer",
                      background:`linear-gradient(to right,${C.sage} 0%,${C.sage} ${(downPayment/Math.max(1,Math.floor(purchasePrice*.5)))*100}%,${C.border} ${(downPayment/Math.max(1,Math.floor(purchasePrice*.5)))*100}%,${C.border} 100%)`}}/>
                </div>
              </div>

              {/* Row 2: Mortgage Rate | Annual Appreciation */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 20px", paddingBottom:"24px" }}>

                {/* Mortgage Rate — slider only, narrow range */}
                <div style={{ marginBottom:"20px" }}>
                  <div style={{fontFamily:"Montserrat,sans-serif",fontSize:"11px",fontWeight:700,
                    letterSpacing:"0.13em",textTransform:"uppercase",color:C.muted,marginBottom:"5px",minHeight:"30px",display:"flex",alignItems:"flex-end"}}>
                    Mortgage Rate
                  </div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:"20px",fontWeight:700,
                    color:C.text,marginBottom:"7px",height:"32px",
                    display:"flex",alignItems:"center"}}>{interestRate.toFixed(2)}%</div>
                  <input type="range" min={2} max={10} step={0.01} value={interestRate}
                    onChange={e=>setInterestRate(Number(e.target.value))}
                    style={{WebkitAppearance:"none",width:"100%",height:"4px",borderRadius:"2px",
                      outline:"none",cursor:"pointer",
                      background:`linear-gradient(to right,${C.sage} 0%,${C.sage} ${((interestRate-2)/8)*100}%,${C.border} ${((interestRate-2)/8)*100}%,${C.border} 100%)`}}/>
                </div>

                {/* Appreciation/yr — slider + benchmark label */}
                <div style={{ marginBottom:"20px" }}>
                  <div style={{fontFamily:"Montserrat,sans-serif",fontSize:"11px",fontWeight:700,
                    letterSpacing:"0.13em",textTransform:"uppercase",color:C.muted,marginBottom:"5px",minHeight:"30px",display:"flex",alignItems:"flex-end"}}>
                    App. Rate/yr
                  </div>
                  <div style={{ display:"flex", alignItems:"center",
                    marginBottom:"7px", height:"32px" }}>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:"20px",
                      fontWeight:700,color:C.text}}>{appRate.toFixed(1)}%</div>
                  </div>
                  <input type="range" min={0} max={8} step={0.1} value={appRate}
                    onChange={e=>setAppRate(Number(e.target.value))}
                    style={{WebkitAppearance:"none",width:"100%",height:"4px",borderRadius:"2px",
                      outline:"none",cursor:"pointer",
                      background:`linear-gradient(to right,${C.sage} 0%,${C.sage} ${(appRate/8)*100}%,${C.border} ${(appRate/8)*100}%,${C.border} 100%)`}}/>
                  <div style={{ display:"flex", justifyContent:"space-between",
                    alignItems:"center", marginTop:"3px" }}>
                    <span style={{fontFamily:"Montserrat,sans-serif",fontSize:"10px",color:C.muted}}>0%</span>
                    <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:"10px",
                      fontWeight:700, color:appBenchmark.color,
                      background:`${appBenchmark.color}18`,
                      borderRadius:"10px", padding:"1px 7px" }}>
                      {appBenchmark.label}
                    </span>
                    <span style={{fontFamily:"Montserrat,sans-serif",fontSize:"10px",color:C.muted}}>8%</span>
                  </div>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"10px",
                    color:C.muted, marginTop:"3px", textAlign:"center" }}>
                    Case-Shiller national avg: ~3.5%/yr
                  </div>
                </div>
              </div>

              {/* Row 3: Monthly Income | Current Value */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 20px" }}>

                {/* Monthly Income */}
                <div style={{ marginBottom:"20px" }}>
                  <div style={{fontFamily:"Montserrat,sans-serif",fontSize:"11px",fontWeight:700,
                    letterSpacing:"0.13em",textTransform:"uppercase",color:C.muted,marginBottom:"5px",minHeight:"30px",display:"flex",alignItems:"flex-end"}}>
                    Monthly Income
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:"2px",
                    marginBottom:"7px", height:"32px" }}>
                    <span style={{fontFamily:"'Playfair Display',serif",fontSize:"20px",fontWeight:700,color:C.text}}>$</span>
                    <input type="number" value={monthlyIncome}
                      onChange={e=>setMonthlyIncome(Number(e.target.value))}
                      onBlur={e=>setMonthlyIncome(Math.max(2000,Math.min(30000,Number(e.target.value)||monthlyIncome)))}
                      style={{fontFamily:"'Playfair Display',serif",fontSize:"20px",fontWeight:700,
                        color:C.text,border:"none",borderBottom:`1px solid ${C.border}`,
                        background:"transparent",outline:"none",width:"100%",
                        MozAppearance:"textfield", height:"32px", padding:0}}/>
                  </div>
                  <input type="range" min={2000} max={30000} step={500} value={monthlyIncome}
                    onChange={e=>setMonthlyIncome(Number(e.target.value))}
                    style={{WebkitAppearance:"none",width:"100%",height:"4px",borderRadius:"2px",
                      outline:"none",cursor:"pointer",
                      background:`linear-gradient(to right,${C.sage} 0%,${C.sage} ${((monthlyIncome-2000)/28000)*100}%,${C.border} ${((monthlyIncome-2000)/28000)*100}%,${C.border} 100%)`}}/>
                </div>

                {/* Current Value */}
                <div style={{ marginBottom:"20px" }}>
                  <div style={{fontFamily:"Montserrat,sans-serif",fontSize:"11px",fontWeight:700,
                    letterSpacing:"0.13em",textTransform:"uppercase",color:C.muted,marginBottom:"5px",minHeight:"30px",display:"flex",alignItems:"flex-end"}}>
                    Current Value
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:"2px",
                    marginBottom:"7px", height:"32px" }}>
                    <span style={{fontFamily:"'Playfair Display',serif",fontSize:"20px",fontWeight:700,color:C.oak}}>$</span>
                    <input type="number" value={currentValue}
                      onChange={e=>setCurrentValue(Number(e.target.value))}
                      onBlur={e=>setCurrentValue(Math.max(Math.floor(purchasePrice*.6),Math.min(purchasePrice*3,Number(e.target.value)||currentValue)))}
                      style={{fontFamily:"'Playfair Display',serif",fontSize:"20px",fontWeight:700,
                        color:C.oak,border:"none",borderBottom:`1px solid ${C.border}`,
                        background:"transparent",outline:"none",width:"100%",
                        MozAppearance:"textfield", height:"32px", padding:0}}/>
                  </div>
                  <input type="range" min={Math.floor(purchasePrice*.6)} max={purchasePrice*3}
                    step={5000} value={currentValue}
                    onChange={e=>setCurrentValue(Number(e.target.value))}
                    style={{WebkitAppearance:"none",width:"100%",height:"4px",borderRadius:"2px",
                      outline:"none",cursor:"pointer",
                      background:`linear-gradient(to right,${C.oak} 0%,${C.oak} ${((currentValue-Math.floor(purchasePrice*.6))/(purchasePrice*3-Math.floor(purchasePrice*.6)))*100}%,${C.border} ${((currentValue-Math.floor(purchasePrice*.6))/(purchasePrice*3-Math.floor(purchasePrice*.6)))*100}%,${C.border} 100%)`}}/>
                  {yearsOwned > 0 && currentValue !== purchasePrice && (
                    <div style={{fontFamily:"Montserrat,sans-serif",fontSize:"12px",color:C.muted,marginTop:"3px"}}>
                      Implied: <strong style={{color: impliedAppRate >= 0 ? C.sage : C.alert}}>
                        {impliedAppRate >= 0 ? "+" : ""}{impliedAppRate.toFixed(1)}%/yr
                      </strong> since {purchaseYear}
                    </div>
                  )}
                </div>
              </div>

              {/* Tax Abatement block */}
              <div style={{ background: hasAbatement ? C.alertBg : C.bg,
                border:`1px solid ${hasAbatement ? "rgba(196,98,45,.22)" : C.border}`,
                borderRadius:"6px", padding:"14px", marginTop:"4px",
                transition:"all 0.2s", opacity: hasAbatement ? 1 : 0.7 }}>

                {/* Header row with checkbox */}
                <div style={{ display:"flex", alignItems:"center",
                  justifyContent:"space-between", marginBottom:"10px" }}>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
                    fontWeight:700, letterSpacing:"0.13em", textTransform:"uppercase",
                    color: hasAbatement ? C.alert : C.muted }}>
                    ⚠ Tax Abatement
                  </div>
                  <label style={{ display:"flex", alignItems:"center", gap:"6px",
                    cursor:"pointer", userSelect:"none" }}>
                    <div style={{ position:"relative", width:"32px", height:"18px" }}>
                      <input type="checkbox" checked={hasAbatement}
                        onChange={e => setHasAbatement(e.target.checked)}
                        style={{ opacity:0, width:0, height:0, position:"absolute" }} />
                      <div style={{ position:"absolute", inset:0, borderRadius:"9px",
                        background: hasAbatement ? C.sage : C.border,
                        transition:"background 0.2s" }} />
                      <div style={{ position:"absolute", top:"2px",
                        left: hasAbatement ? "16px" : "2px",
                        width:"14px", height:"14px", borderRadius:"50%",
                        background:"white", boxShadow:"0 1px 3px rgba(0,0,0,.2)",
                        transition:"left 0.2s" }} />
                    </div>
                    <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
                      color: C.muted }}>
                      {hasAbatement ? "On" : "Off"}
                    </span>
                  </label>
                </div>

                {/* Controls — greyed when off */}
                <div style={{ pointerEvents: hasAbatement ? "auto" : "none" }}>
                  <div style={{ marginBottom:"10px" }}>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
                      color:C.muted, marginBottom:"4px" }}>Expiry Year</div>
                    <select value={abatExpiry} onChange={e=>setAbatExpiry(Number(e.target.value))}
                      disabled={!hasAbatement}
                      style={{ fontFamily:"Montserrat,sans-serif", fontSize:"13px",
                        background:"white", border:`1px solid ${C.border}`,
                        borderRadius:"4px", padding:"8px 30px 8px 10px",
                        color: hasAbatement ? C.text : C.muted,
                        width:"100%", cursor: hasAbatement ? "pointer" : "default",
                        appearance:"none",
                        backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%238A8678'/%3E%3C/svg%3E")`,
                        backgroundRepeat:"no-repeat", backgroundPosition:"right 10px center" }}>
                      {Array.from({length:25},(_,i)=>CURRENT_YEAR-2+i).map(y=>(
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ display:"flex", gap:"10px" }}>
                    {[
                      ["Tax During Abatement","$",currentMonthlyTax,setCurrentMonthlyTax,0,2000],
                      ["Full Tax After Cliff","$",postCliffMonthlyTax,setPostCliffMonthlyTax,0,3000],
                    ].map(([label,prefix,val,setter,min,max])=>{
                      const pct=((val-min)/(max-min))*100;
                      return (
                        <div key={label} style={{flex:1}}>
                          <div style={{fontFamily:"Montserrat,sans-serif",fontSize:"12px",
                            color:C.muted,marginBottom:"3px"}}>{label}</div>
                          <div style={{fontFamily:"'Playfair Display',serif",fontSize:"16px",
                            fontWeight:700,color: hasAbatement ? C.text : C.muted,
                            marginBottom:"4px"}}>{prefix}{val}/mo</div>
                          <input type="range" min={min} max={max} step={25} value={val}
                            onChange={e=>setter(Number(e.target.value))}
                            style={{WebkitAppearance:"none",width:"100%",height:"3px",
                              borderRadius:"2px",outline:"none",
                              cursor: hasAbatement ? "pointer" : "default",
                              background:`linear-gradient(to right,${hasAbatement?C.alert:C.muted} 0%,${hasAbatement?C.alert:C.muted} ${pct}%,${C.border} ${pct}%,${C.border} 100%)`}} />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {hasAbatement && cliffActive && (
                  <div style={{marginTop:"10px",display:"inline-flex",alignItems:"center",
                    gap:"6px",background:"white",border:`1px solid ${C.alert}`,color:C.alert,
                    borderRadius:"3px",padding:"5px 11px",
                    fontFamily:"Montserrat,sans-serif",fontSize:"13px",fontWeight:700}}>
                    ⚠ +${taxIncrease}/mo in {yearsToCliff} yr{yearsToCliff!==1?"s":""}
                  </div>
                )}
              </div>
            </div>

            {/* ── VISUALIZATION ── */}
            <div style={{ flex:1, padding:isMobile?"16px":"24px 28px", minWidth:0,
              display:"flex", flexDirection:"column", gap:"18px" }}>

              {/* Gauge + DTI row */}
              <div style={{ display:"flex", gap:"14px",
                flexDirection:isMobile?"column":"row", alignItems:"stretch" }}>
                <div style={{ background:C.bg, border:`1px solid ${C.border}`,
                  borderRadius:"6px", padding:isMobile?"14px":"16px 20px",
                  textAlign:"center", width:isMobile?"100%":"auto" }}>
                  <Gauge value={accessibleEquity} maxValue={currentValue*.55}
                    monthlyPayment={helocPayment} helocRate={helocRate} />
                </div>
                <DTIPanel
                  cliffActive={cliffActive} hasAbatement={hasAbatement}
                  abatExpiry={abatExpiry} yearsToCliff={yearsToCliff}
                  currentDTI={currentDTI} postCliffDTI={postCliffDTI}
                  taxIncrease={taxIncrease}
                  dtiWithHeloc={dtiWithHeloc} helocPayment={helocPayment} />
              </div>

              {/* Payment Estimator */}
              <PaymentCard
                accessibleEquity={accessibleEquity}
                borrowAmount={Math.min(borrowAmount, accessibleEquity)}
                setBorrowAmount={setBorrowAmount}
                helocRate={helocRate} setHelocRate={setHelocRate}
                monthlyPayment={helocPayment} />

              {/* ── HELOC IMPACT SCENARIOS ── */}
              <div style={{ background:C.paper, border:`1px solid ${C.border}`,
                borderRadius:"6px", padding:"20px 22px" }}>

                {/* Header row */}
                <div style={{ marginBottom:"16px" }}>
                  <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"22px",
                    fontWeight:600, color:C.text }}>
                    HELOC Impact on Your Equity
                  </div>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
                    color:C.muted, marginTop:"2px" }}>
                    See how repayment strategy changes your net equity over time
                  </div>
                </div>

                {/* Scenario selector */}
                <div style={{ display:"grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                  gap:"10px", marginBottom:"18px" }}>
                  {[
                    { id:1, label:"Interest-Only Draw",
                      desc:`Pay $${helocCalc.drawMonthly}/mo during draw period. Full balance remains at year ${drawPeriod}.`,
                      warning: true },
                    { id:2, label:"Pay Down During Draw",
                      desc:`Pay interest + extra principal each month. Reduces repayment shock.`,
                      warning: false },
                    { id:3, label:"Pay Off in Draw Period",
                      desc:`Fully amortize the HELOC during the ${drawPeriod}-yr draw period. No repayment phase.`,
                      warning: false },
                  ].map(s => (
                    <div key={s.id}
                      onClick={() => setHelocScenario(s.id)}
                      style={{ cursor:"pointer", borderRadius:"6px", padding:"13px 14px",
                        border:`2px solid ${helocScenario === s.id
                          ? (s.warning ? C.alert : C.sage)
                          : C.border}`,
                        background: helocScenario === s.id
                          ? (s.warning ? C.alertBg : C.greenBg)
                          : C.bg,
                        transition:"all 0.15s" }}>
                      <div style={{ display:"flex", alignItems:"center",
                        gap:"6px", marginBottom:"5px" }}>
                        <div style={{ width:"14px", height:"14px", borderRadius:"50%",
                          border:`2px solid ${helocScenario === s.id
                            ? (s.warning ? C.alert : C.sage) : C.border}`,
                          background: helocScenario === s.id
                            ? (s.warning ? C.alert : C.sage) : "transparent",
                          flexShrink:0 }} />
                        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"13px",
                          fontWeight:700, color: helocScenario === s.id
                            ? (s.warning ? C.alert : C.sage) : C.text }}>
                          {s.warning ? "⚠ " : "✓ "}{s.label}
                        </div>
                      </div>
                      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
                        color:C.muted, lineHeight:1.5, paddingLeft:"20px" }}>
                        {s.desc}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Scenario-specific controls */}
                <div style={{ display:"flex", gap:"16px", flexWrap:"wrap", marginBottom:"16px" }}>
                  {/* Draw period */}
                  {[
                    ["Draw Period", `${drawPeriod} yrs`, drawPeriod, setDrawPeriod, 5, 15, 1, C.sage],
                    ["Repayment Period", `${repayPeriod} yrs`, repayPeriod, setRepayPeriod, 5, 20, 5, C.sage],
                  ].map(([label, display, val, setter, min, max, step, accent]) => {
                    const pct = ((val-min)/(max-min))*100;
                    return (
                      <div key={label} style={{ flex:"1 1 140px" }}>
                        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
                          fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase",
                          color:C.muted, marginBottom:"4px" }}>{label}</div>
                        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"18px",
                          fontWeight:700, color:C.text, marginBottom:"6px" }}>{display}</div>
                        <input type="range" min={min} max={max} step={step} value={val}
                          onChange={e => setter(Number(e.target.value))}
                          style={{ WebkitAppearance:"none", width:"100%", height:"4px",
                            borderRadius:"2px", outline:"none", cursor:"pointer",
                            background:`linear-gradient(to right,${accent} 0%,${accent} ${pct}%,${C.border} ${pct}%,${C.border} 100%)` }} />
                      </div>
                    );
                  })}

                  {/* Scenario 2: extra principal slider */}
                  {helocScenario === 2 && (
                    <div style={{ flex:"1 1 140px" }}>
                      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
                        fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase",
                        color:C.muted, marginBottom:"4px" }}>Extra Principal/Mo</div>
                      <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"18px",
                        fontWeight:700, color:C.text, marginBottom:"6px" }}>
                        +${extraPrincipal}/mo
                      </div>
                      <input type="range" min={50} max={1000} step={25}
                        value={extraPrincipal}
                        onChange={e => setExtraPrincipal(Number(e.target.value))}
                        style={{ WebkitAppearance:"none", width:"100%", height:"4px",
                          borderRadius:"2px", outline:"none", cursor:"pointer",
                          background:`linear-gradient(to right,${C.sage} 0%,${C.sage}
                            ${((extraPrincipal-50)/950)*100}%,${C.border}
                            ${((extraPrincipal-50)/950)*100}%,${C.border} 100%)` }} />
                    </div>
                  )}
                </div>

                {/* Repayment shock summary */}
                <div style={{ display:"flex", gap:"10px", flexWrap:"wrap" }}>

                  {/* Draw phase */}
                  <div style={{ flex:"1 1 140px", background:C.bg, borderRadius:"5px",
                    padding:"12px 14px", border:`1px solid ${C.border}` }}>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
                      fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase",
                      color:C.muted, marginBottom:"4px" }}>
                      During Draw ({drawPeriod} yrs)
                    </div>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"22px",
                      fontWeight:700, color:C.sage }}>
                      ${helocScenario === 1
                          ? helocCalc.drawMonthly.toLocaleString()
                          : helocScenario === 2
                            ? (helocCalc.drawMonthly + extraPrincipal).toLocaleString()
                            : helocCalc.s3Monthly.toLocaleString()
                        }/mo
                    </div>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
                      color:C.muted, marginTop:"3px" }}>
                      {helocScenario === 1 ? "Interest only"
                        : helocScenario === 2 ? `Interest + $${extraPrincipal} principal`
                        : "Fully amortizing"}
                    </div>
                  </div>

                  {/* Balance at end of draw */}
                  <div style={{ flex:"1 1 140px", background:C.bg, borderRadius:"5px",
                    padding:"12px 14px",
                    border:`1px solid ${helocCalc.balAtDrawEnd > 0 ? "rgba(196,98,45,.25)" : C.border}` }}>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
                      fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase",
                      color:C.muted, marginBottom:"4px" }}>
                      Balance at Draw End ({helocCalc.drawEndYear})
                    </div>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"22px",
                      fontWeight:700,
                      color: helocCalc.balAtDrawEnd === 0 ? C.green
                        : helocCalc.balAtDrawEnd < borrowAmount * 0.5 ? C.oak : C.alert }}>
                      ${helocCalc.balAtDrawEnd.toLocaleString()}
                    </div>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
                      color:C.muted, marginTop:"3px" }}>
                      {helocCalc.balAtDrawEnd === 0 ? "✓ Fully paid off"
                        : `${Math.round(helocCalc.balAtDrawEnd/borrowAmount*100)}% of original balance`}
                    </div>
                  </div>

                  {/* Repayment phase payment */}
                  {helocCalc.balAtDrawEnd > 0 && (
                    <div style={{ flex:"1 1 140px",
                      background: helocScenario === 1 ? C.alertBg : C.bg,
                      borderRadius:"5px", padding:"12px 14px",
                      border:`1px solid ${helocScenario === 1 ? "rgba(196,98,45,.35)" : C.border}` }}>
                      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
                        fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase",
                        color: helocScenario === 1 ? C.alert : C.muted, marginBottom:"4px" }}>
                        {helocScenario === 1 ? "⚠ Repayment Shock" : "Repayment Phase"}
                      </div>
                      <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"22px",
                        fontWeight:700,
                        color: helocScenario === 1 ? C.alert : C.oak }}>
                        ${helocCalc.repayMonthly.toLocaleString()}/mo
                      </div>
                      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
                        color:C.muted, marginTop:"3px" }}>
                        {repayPeriod}-yr repayment · starts {helocCalc.drawEndYear}
                      </div>
                      {helocScenario === 1 && (
                        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"13px",
                          color:C.alert, marginTop:"6px", lineHeight:1.5 }}>
                          ${(helocCalc.repayMonthly - helocCalc.drawMonthly).toLocaleString()}/mo
                          more than your draw-period payment
                        </div>
                      )}
                    </div>
                  )}

                  {/* Total cost */}
                  <div style={{ flex:"1 1 140px", background:C.bg,
                    borderRadius:"5px", padding:"12px 14px",
                    border:`1px solid ${helocScenario === 3 ? C.sage+"55" : C.border}` }}>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
                      fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase",
                      color: helocScenario === 3 ? C.sage : C.muted, marginBottom:"4px" }}>
                      Total Cost
                    </div>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"22px",
                      fontWeight:700, color: helocScenario === 3 ? C.sage : C.text }}>
                      ${helocCalc.totalCost.toLocaleString()}
                    </div>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
                      color:C.muted, marginTop:"3px" }}>
                      Total paid across {helocScenario === 3 ? "draw period" : "both phases"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div style={{ background:C.paper }}>
                <div style={{ display:"flex", alignItems:"center",
                  justifyContent:"space-between", marginBottom:"12px", flexWrap:"wrap", gap:"8px" }}>
                  <div>
                    <div style={{ fontFamily:"'Playfair Display',serif",
                      fontSize:isMobile?"16px":"19px", fontWeight:700, color:C.text }}>
                      Your 30-Year Equity Projection
                    </div>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
                      color:C.muted, marginTop:"2px" }}>
                      {purchaseYear} → {purchaseYear+30} · {appRate.toFixed(1)}% appreciation/yr
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
                    {/* Post-cliff toggle */}
                    {hasCliff && (
                      <button className="toggle-btn"
                        onClick={() => setShowPostCliff(v => !v)}
                        style={{
                          background: showPostCliff ? C.alert : "transparent",
                          color: showPostCliff ? "white" : C.alert,
                          borderColor: C.alert,
                        }}>
                        {showPostCliff ? "✓ Post-Cliff View" : "Show Post-Cliff"}
                      </button>
                    )}
                  </div>
                </div>

                {/* HELOC overlay toggle — lives above the chart */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                  marginBottom:"12px", flexWrap:"wrap", gap:"8px" }}>
                  <label style={{ display:"flex", alignItems:"center", gap:"8px",
                    cursor:"pointer", userSelect:"none" }}>
                    <div style={{ position:"relative", width:"36px", height:"20px" }}>
                      <input type="checkbox" checked={showHelocOverlay}
                        onChange={e => setShowHelocOverlay(e.target.checked)}
                        style={{ opacity:0, width:0, height:0, position:"absolute" }} />
                      <div style={{ position:"absolute", inset:0, borderRadius:"10px",
                        background: showHelocOverlay ? "#E07050" : C.border,
                        transition:"background 0.2s" }} />
                      <div style={{ position:"absolute", top:"3px",
                        left: showHelocOverlay ? "18px" : "3px",
                        width:"14px", height:"14px", borderRadius:"50%",
                        background:"white", boxShadow:"0 1px 3px rgba(0,0,0,.2)",
                        transition:"left 0.2s" }} />
                    </div>
                    <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
                      fontWeight:600, color: showHelocOverlay ? "#E07050" : C.muted }}>
                      {showHelocOverlay ? "HELOC Impact: On" : "Show HELOC Impact on Chart"}
                    </span>
                  </label>

                  {!isMobile && (
                    <div style={{ display:"flex", gap:"12px", flexWrap:"wrap", alignItems:"center" }}>
                      {[
                        {color:C.sage,  label:"Mortgage Balance"},
                        {color:C.cream, label:"Your Equity", stroke:C.oak},
                        ...(showHelocOverlay ? [{color:"#E07050", label:"HELOC Balance"}] : []),
                        ...(showPostCliff && hasCliff ? [{dashed:true, color:C.alert, label:"Post-Cliff"}] : []),
                        ...(pmiCrossoverYear ? [{dashed:true, color:C.oak, label:"PMI Drops"}] : [])
                      ].map(({color,label,dashed,stroke})=>(
                        <div key={label} style={{display:"flex",alignItems:"center",gap:"5px"}}>
                          {dashed
                            ? <svg width="16" height="10"><line x1="0" y1="5" x2="16" y2="5"
                                stroke={color} strokeWidth="2" strokeDasharray="4 2"/></svg>
                            : <div style={{width:"14px",height:"10px",borderRadius:"2px",
                                background:color, border: stroke ? `1.5px solid ${stroke}` : "none"}}/>}
                          <span style={{fontFamily:"Montserrat,sans-serif",fontSize:"12px",color:C.muted}}>{label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div ref={chartRef} style={{ background:C.paper, position:"relative" }}>
                  {/* foundHE watermark — captured by html2canvas for PDF automatically */}
                  <div style={{
                    position:"absolute", bottom:"34px", right:"34px",
                    fontFamily:"'Playfair Display',serif", fontWeight:700,
                    fontSize:"20px", opacity:0.25, pointerEvents:"none",
                    userSelect:"none", zIndex:1
                  }}>
                    <span style={{color:C.sageDark}}>found</span>
                    <span style={{color:C.oak}}>HE</span>
                  </div>
                <ResponsiveContainer width="100%" height={isMobile?250:310}>
                  <ComposedChart data={chartData} margin={{top:30,right:8,left:0,bottom:0}}>
                    <defs>
                      <linearGradient id="gSage" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.sage} stopOpacity={0.92}/>
                        <stop offset="100%" stopColor={C.sageDark} stopOpacity={0.8}/>
                      </linearGradient>
                      <linearGradient id="gOak" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.oak} stopOpacity={0.88}/>
                        <stop offset="100%" stopColor={C.oak} stopOpacity={0.72}/>
                      </linearGradient>
                      <linearGradient id="gCream" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.cream} stopOpacity={0.82}/>
                        <stop offset="100%" stopColor={C.cream} stopOpacity={0.55}/>
                      </linearGradient>
                      <linearGradient id="gHeloc" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#E07050" stopOpacity={0.75}/>
                        <stop offset="100%" stopColor="#C4622D" stopOpacity={0.55}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                    <XAxis dataKey="label"
                      tick={{fontSize:9,fill:C.muted,fontFamily:"Montserrat,sans-serif"}}
                      tickLine={false} axisLine={{stroke:C.border}}
                      interval={isMobile?9:4}/>
                    <YAxis tickFormatter={fmt$}
                      tick={{fontSize:9,fill:C.muted,fontFamily:"Montserrat,sans-serif"}}
                      tickLine={false} axisLine={false} width={36}/>
                    <Tooltip content={<ChartTooltip/>}/>

                    {/* Optimal window shading */}
                    {cliffActive && (
                      <ReferenceArea
                        x1={String(CURRENT_YEAR)} x2={String(abatExpiry)}
                        fill={C.sage} fillOpacity={0.07}
                        stroke={C.sage} strokeOpacity={0.15} />
                    )}

                    {/* Post-cliff dimming */}
                    {showPostCliff && hasCliff && (
                      <ReferenceArea
                        x1={String(abatExpiry)} x2={String(purchaseYear+30)}
                        fill={C.alert} fillOpacity={0.05}
                        stroke={C.alert} strokeOpacity={0.1} />
                    )}

                    {/* Mortgage balance area — fills 0→balance, shrinks over time */}
                    <Area type="monotone" dataKey="balance"
                      stroke={C.sageDark} strokeWidth={1}
                      fill="url(#gSage)" fillOpacity={0.85}
                      name="Mortgage Balance" dot={false}/>

                    {/* Total equity — tan shaded area with gold line on top */}
                    <Area type="monotone" dataKey="totalEquity"
                      stroke={C.oak} strokeWidth={2.5}
                      fill="url(#gCream)" fillOpacity={0.7}
                      name="Total Equity" dot={false}/>

                    {/* HELOC balance overlay */}
                    {showHelocOverlay && (
                      <Area type="monotone" dataKey="helocBalance"
                        stroke="#E07050" strokeWidth={1.5}
                        fill="url(#gHeloc)" fillOpacity={0.7}
                        name="HELOC Balance" dot={false}/>
                    )}

                    {/* Post-cliff equity scenario line */}
                    {showPostCliff && hasCliff && (
                      <Line type="monotone" dataKey="postCliffEquity"
                        stroke={C.alert} strokeWidth={1.5}
                        strokeDasharray="4 3" dot={false} name="Post-Cliff Equity"/>
                    )}

                    {/* TODAY */}
                    {yearsOwned>=0 && yearsOwned<=30 && (
                      <ReferenceLine x={String(CURRENT_YEAR)}
                        stroke={C.sageDark} strokeWidth={2}
                        label={{value:"TODAY",position:"top",fill:C.sageDark,
                          fontSize:8,fontFamily:"Montserrat,sans-serif",
                          fontWeight:700,letterSpacing:"0.1em"}}/>
                    )}

                    {/* HELOC draw period end */}
                    {showHelocOverlay && helocCalc.drawEndYear <= purchaseYear+30 && (
                      <ReferenceLine x={String(helocCalc.drawEndYear)}
                        stroke="#E07050" strokeWidth={1.5} strokeDasharray="4 3"
                        label={{value:"DRAW END",position:"top",fill:"#E07050",
                          fontSize:8,fontFamily:"Montserrat,sans-serif",fontWeight:700}}/>
                    )}

                    {/* HELOC fully paid off */}
                    {showHelocOverlay && helocCalc.balAtDrawEnd > 0 &&
                      helocCalc.repayEndYear <= purchaseYear+30 && (
                      <ReferenceLine x={String(helocCalc.repayEndYear)}
                        stroke="#E07050" strokeWidth={1.5} strokeDasharray="4 3"
                        label={{value:"HELOC PAID OFF",position:"top",fill:"#E07050",
                          fontSize:8,fontFamily:"Montserrat,sans-serif",fontWeight:700}}/>
                    )}

                    {/* TAX CLIFF */}
                    {hasCliff && (
                      <ReferenceLine x={String(abatExpiry)}
                        stroke={C.alert} strokeWidth={2} strokeDasharray="6 4"
                        label={{value:"⚠ TAX CLIFF",position:"top",fill:C.alert,
                          fontSize:8,fontFamily:"Montserrat,sans-serif",fontWeight:700}}/>
                    )}

                    {/* PMI crossover — when balance drops below 80% of purchase price */}
                    {pmiCrossoverYear && pmiCrossoverYear > CURRENT_YEAR && (
                      <ReferenceLine x={String(pmiCrossoverYear)}
                        stroke={C.oak} strokeWidth={1.5} strokeDasharray="3 3"
                        label={{value:"✓ PMI DROPS",position:"top",fill:C.oak,
                          fontSize:8,fontFamily:"Montserrat,sans-serif",fontWeight:700}}/>
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
                </div>{/* end chartRef */}

                {/* Mobile legend */}
                {isMobile && (
                  <div style={{display:"flex",gap:"12px",flexWrap:"wrap",
                    marginTop:"10px",justifyContent:"center"}}>
                    {[{color:C.sage,label:"Mortgage Balance"},
                      {color:C.cream,label:"Your Equity"},
                      ...(showHelocOverlay?[{color:"#E07050",label:"HELOC"}]:[])
                    ].map(({color,label})=>(
                      <div key={label} style={{display:"flex",alignItems:"center",gap:"5px"}}>
                        <div style={{width:"10px",height:"10px",borderRadius:"2px",background:color}}/>
                        <span style={{fontFamily:"Montserrat,sans-serif",fontSize:"12px",color:C.muted}}>{label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div style={{ padding:isMobile?"0 16px 40px":"0 48px 52px" }}>
          <div style={{ background:C.sageDark, borderRadius:"8px",
            padding:isMobile?"28px 18px":"40px 52px", textAlign:"center" }}>
            <div style={{ fontFamily:"'Playfair Display',serif",
              fontSize:isMobile?"20px":"24px", fontWeight:700,
              color:"white", lineHeight:1.35, marginBottom:"10px" }}>
              Get your free equity summary and<br/>explore current HELOC options.
            </div>
            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
              color:"rgba(255,255,255,.5)", marginBottom:"22px",
              maxWidth:"400px", margin:"0 auto 22px", lineHeight:1.7 }}>
              No application. No obligation. Just your numbers.
            </div>
            {helocPayment > 0 && (
              <div style={{ background:"rgba(255,255,255,.07)", borderRadius:"6px",
                padding:"14px 20px", maxWidth:"420px", margin:"0 auto 20px",
                fontFamily:"Montserrat,sans-serif", fontSize:"13px",
                color:"rgba(255,255,255,.7)", lineHeight:1.7 }}>
                Based on your inputs: borrow <strong style={{color:C.oakLight}}>
                ${borrowAmount.toLocaleString()}</strong> at ~{helocRate.toFixed(1)}% APR
                = <strong style={{color:C.oakLight}}>${helocPayment}/mo</strong> interest-only.
                Enter your email to save this scenario and compare real lender options.
              </div>
            )}
            <div style={{ display:"flex", flexDirection:"column", gap:"10px",
              maxWidth:isMobile?"100%":"420px", margin:"0 auto 12px" }}>
              {emailStatus === "success" ? (
                <div style={{ background:"rgba(255,255,255,.12)", borderRadius:"6px",
                  padding:"24px 20px", textAlign:"center" }}>
                  <div style={{ fontSize:"32px", marginBottom:"8px" }}>✅</div>
                  <div style={{ fontFamily:"'Playfair Display',serif", fontSize:"18px",
                    fontWeight:600, color:"white", marginBottom:"6px" }}>
                    Check your inbox!
                  </div>
                  <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"12px",
                    color:"rgba(255,255,255,.6)", lineHeight:1.6 }}>
                    Your equity summary PDF is on its way to <strong>{email}</strong>
                  </div>
                </div>
              ) : (
                <>
                  <input type="email" placeholder="Enter your email address"
                    value={email} onChange={e=>{ setEmail(e.target.value); setEmailError(""); }}
                    className="email-inp"/>
                  <div style={{ display:"flex", gap:"10px", alignItems:"stretch" }}>
                    <input type="text" placeholder="Zip code (optional)"
                      value={zipCode} onChange={e=>setZipCode(e.target.value)}
                      maxLength={5} className="email-inp"
                      style={{ flex:"0 0 140px", fontSize:"13px" }}/>
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
                      color:"rgba(255,255,255,.35)", lineHeight:1.4,
                      display:"flex", alignItems:"center", flex:1 }}>
                      Helps us show relevant local rates — never required
                    </div>
                  </div>
                  {emailError && (
                    <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
                      color:C.oakLight, textAlign:"center" }}>{emailError}</div>
                  )}
                  <button className="cta-btn"
                    onClick={handleSubmit}
                    disabled={emailStatus === "loading"}
                    style={{ opacity:emailStatus==="loading"?0.7:1,
                      cursor:emailStatus==="loading"?"wait":"pointer" }}>
                    {emailStatus === "loading" ? "Generating your report..." : "Get My Free Summary"}
                  </button>
                </>
              )}
            </div>
            <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:"11px",
              color:"rgba(255,255,255,.3)", letterSpacing:"0.04em", lineHeight:1.6 }}>
              We never sell your data. Submitting your email does not start an application or obligation of any kind.
            </div>
          </div>
          <div style={{ textAlign:"center", marginTop:"20px",
            fontFamily:"Montserrat,sans-serif", fontSize:"12px", color:C.muted }}>
            foundHE · Estimates only, not financial advice · © {CURRENT_YEAR}
          </div>
        </div>

          </div>
      </div>
    </>
  );
}